import classNames from 'classnames/bind';
import { List, ListItemButton, Skeleton } from '@mui/material';
import { BillingWeekSummaries, SummaryRow, currencyFormatter } from './types';

import styles from './SummaryPanel.module.css';

const cx = classNames.bind(styles);

export interface SummarySelection {
  service: string | null;
  day: string | null;
  customer: string | null;
}

interface SummarySectionProps {
  title: string;
  rows: SummaryRow[];
  total: number;
  loading: boolean;
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
}

function SummarySection(props: SummarySectionProps) {
  return (
    <div className={styles.section}>
      <div className={styles['section-title']}>{props.title}</div>

      <div className={styles['section-rows']}>
        {props.loading ? (
          <div className={styles['skeleton-rows']}>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        ) : (
          <List
            dense
            disablePadding>
            {props.rows.map((row) => (
              <ListItemButton
                key={row.key}
                dense
                selected={props.selectedKey === row.key}
                onClick={() => {
                  props.onSelect(props.selectedKey === row.key ? null : row.key);
                }}>
                <span className={styles['row-label']}>{row.label}</span>
                <span className={cx({ 'row-amount': true, negative: row.amount < 0 })}>{currencyFormatter.format(row.amount)}</span>
              </ListItemButton>
            ))}
          </List>
        )}
      </div>

      <div className={styles['total-row']}>
        <span className={styles['row-label']}>Total</span>
        <span className={cx({ 'row-amount': true, negative: props.total < 0 })}>
          {props.loading ? '—' : currencyFormatter.format(props.total)}
        </span>
      </div>
    </div>
  );
}

interface SummaryPanelProps {
  summaries: BillingWeekSummaries;
  loading: boolean;
  selection: SummarySelection;
  onSelectionChange: (selection: SummarySelection) => void;
}

export function SummaryPanel({ summaries, loading, selection, onSelectionChange }: SummaryPanelProps) {
  return (
    <div className={styles.panel}>
      <SummarySection
        title='By Service'
        rows={summaries.byService}
        total={summaries.total}
        loading={loading}
        selectedKey={selection.service}
        onSelect={(service) => {
          onSelectionChange({ ...selection, service });
        }}
      />
      <SummarySection
        title='By Day'
        rows={summaries.byDay}
        total={summaries.total}
        loading={loading}
        selectedKey={selection.day}
        onSelect={(day) => {
          onSelectionChange({ ...selection, day });
        }}
      />
      <SummarySection
        title='By Customer'
        rows={summaries.byCustomer}
        total={summaries.total}
        loading={loading}
        selectedKey={selection.customer}
        onSelect={(customer) => {
          onSelectionChange({ ...selection, customer });
        }}
      />
    </div>
  );
}
